import React from 'react'

import { Routes, Route, Navigate } from 'react-router-dom'

import { BaseLayout, InnerPageLayout } from '@app/layouts'
import {
  AuthPage,
  ErrorPage,
  ContractorSectionPage,
  ContractorSectionPageInner,
  MonitoringLaunchPage,
  MonitoringRegistryPage,
  ProcessingResultsPage,
  ProcessingResultsPageInner,
  RegistryPage,
  RegistryPageInner,
  RegistryPageArchivesInner,
  QuotaReportPage,
  ExternalProductsCatalogPage,
  InternalTasksPage,
  MonitoringSchedulePage,
  DirectoryContractorsPage,
  ManagingSubtypesPage,
  ManagingMonitoringsPage,
  TemplatesEditorPage,
  CheckingPricesCompetitorsPage,
  TransferResultsPage,
  ClassifierControlsPage
} from '@app/pages'

import {
  AUTH_PAGE,
  BASE_PAGE,
  CONTRACTOR_SECTION_PAGE,
  MONITORING_LAUNCH_PAGE,
  MONITORING_REGISTRY_PAGE,
  PROCESSING_RESULTS_PAGE,
  REGISTRY_PAGE,
  QUOTE_REPORT_PAGE,
  ERROR_PAGE,
  EXTERNAL_PRODUCTS_CATALOG_PAGE,
  INTERNAL_TASKS_PAGE,
  SETTINGS_PAGE,
  TRANSFER_RESULTS_PAGE
} from '@app/constants/page'

import { AuthRoute, PermissionRoute } from '@app/routes'

import { ROLE } from '@app/constants/permission'

const layout = (
  <AuthRoute>
    <BaseLayout />
  </AuthRoute>
)

const App: React.FC = () => {
  const element = (
    <Navigate
      to={ AUTH_PAGE.auth }
      replace
    />
  )

  return (
    <Routes>
      <Route
        path={ AUTH_PAGE.auth }
        element={ <AuthPage /> }
      />

      <Route
        path={ BASE_PAGE.root }
        element={ element }
      />

      <Route element={ layout }>
        <Route
          path={ BASE_PAGE.all }
          element={
            <Navigate
              to={ ERROR_PAGE }
              replace
            />
          }
        />

        <Route
          path={ ERROR_PAGE }
          element={ <ErrorPage /> }
        />

        <Route
          path={ REGISTRY_PAGE.registry }
          element={
            <PermissionRoute
              requiredRoles={ [
                ROLE.priceAnalyticsSpecialist,
                ROLE.pricingSpecialist,
                ROLE.commercialServiceSpecialist,
                ROLE.promoPlanningSpecialist,
                ROLE.commercialServiceDirector
              ] }
            >
              <RegistryPage />
            </PermissionRoute>
          }
        />

        <Route
          path={ REGISTRY_PAGE.registryId }
          element={
            <PermissionRoute
              requiredRoles={ [
                ROLE.priceAnalyticsSpecialist,
                ROLE.pricingSpecialist,
                ROLE.commercialServiceSpecialist,
                ROLE.promoPlanningSpecialist,
                ROLE.commercialServiceDirector
              ] }
            >
              <InnerPageLayout
                type='number'
                render={ id => <RegistryPageInner id={ id } /> }
              />
            </PermissionRoute>
          }
        />

        <Route
          path={ REGISTRY_PAGE.registryArchivesId }
          element={
            <PermissionRoute
              requiredRoles={ [
                ROLE.priceAnalyticsSpecialist,
                ROLE.pricingSpecialist,
                ROLE.commercialServiceSpecialist,
                ROLE.promoPlanningSpecialist,
                ROLE.commercialServiceDirector
              ] }
            >
              <InnerPageLayout
                type='number'
                render={ id => <RegistryPageArchivesInner id={ id } /> }
              />
            </PermissionRoute>
          }
        />

        <Route
          path={ MONITORING_LAUNCH_PAGE.monitoringLaunch }
          element={
            <PermissionRoute requiredRoles={ [ROLE.priceAnalyticsSpecialist] }>
              <MonitoringLaunchPage />
            </PermissionRoute>
          }
        />

        <Route
          path={ MONITORING_REGISTRY_PAGE.monitoringRegistry }
          element={
            <PermissionRoute requiredRoles={ [ROLE.priceAnalyticsSpecialist, ROLE.pricingSpecialist] }>
              <MonitoringRegistryPage />
            </PermissionRoute>
          }
        />

        <Route
          path={ CONTRACTOR_SECTION_PAGE.contractorSectionId }
          element={
            <PermissionRoute requiredRoles={ [ROLE.contractor] }>
              <InnerPageLayout
                type='number'
                render={ id => <ContractorSectionPageInner id={ id } /> }
              />
            </PermissionRoute>
          }
        />

        <Route
          path={ CONTRACTOR_SECTION_PAGE.contractorSection }
          element={
            <PermissionRoute requiredRoles={ [ROLE.contractor] }>
              <ContractorSectionPage />
            </PermissionRoute>
          }
        />

        <Route
          path={ PROCESSING_RESULTS_PAGE.processingResults }
          element={
            <PermissionRoute
              requiredRoles={ [
                ROLE.priceAnalyticsSpecialist,
                ROLE.pricingSpecialist,
                ROLE.categoryManager,
                ROLE.promoPlanningSpecialist,
                ROLE.commercialServiceDirector,
                ROLE.commercialServiceSpecialist
              ] }
            >
              <ProcessingResultsPage />
            </PermissionRoute>
          }
        />

        <Route
          path={ TRANSFER_RESULTS_PAGE.transferResults }
          element={
            <PermissionRoute requiredRoles={ [ROLE.priceAnalyticsSpecialist, ROLE.pricingSpecialist] }>
              <TransferResultsPage />
            </PermissionRoute>
          }
        />

        <Route
          path={ PROCESSING_RESULTS_PAGE.productCard }
          element={
            <PermissionRoute
              requiredRoles={ [
                ROLE.priceAnalyticsSpecialist,
                ROLE.pricingSpecialist,
                ROLE.categoryManager,
                ROLE.promoPlanningSpecialist,
                ROLE.commercialServiceDirector,
                ROLE.commercialServiceSpecialist
              ] }
            >
              <InnerPageLayout
                type='number'
                render={ id => <ProcessingResultsPageInner id={ id } /> }
              />
            </PermissionRoute>
          }
        />

        <Route
          path={ QUOTE_REPORT_PAGE.quoteReport }
          element={
            <PermissionRoute
              requiredRoles={ [
                ROLE.priceAnalyticsSpecialist,
                ROLE.pricingSpecialist,
                ROLE.commercialServiceSpecialist,
                ROLE.promoPlanningSpecialist,
                ROLE.commercialServiceDirector
              ] }
            >
              <QuotaReportPage />
            </PermissionRoute>
          }
        />

        <Route
          path={ EXTERNAL_PRODUCTS_CATALOG_PAGE.externalProductsCatalog }
          element={
            <PermissionRoute
              requiredRoles={ [
                ROLE.priceAnalyticsSpecialist,
                ROLE.pricingSpecialist,
                ROLE.categoryManager,
                ROLE.commercialServiceDirector,
                ROLE.commercialServiceSpecialist
              ] }
            >
              <ExternalProductsCatalogPage />
            </PermissionRoute>
          }
        />

        <Route
          path={ INTERNAL_TASKS_PAGE.internalTasks }
          element={
            <PermissionRoute
              requiredRoles={ [
                ROLE.priceAnalyticsSpecialist,
                ROLE.pricingSpecialist,
                ROLE.commercialServiceSpecialist,
                ROLE.commercialServiceDirector,
                ROLE.promoPlanningSpecialist
              ] }
            >
              <InternalTasksPage />
            </PermissionRoute>
          }
        />

        <Route
          path={ SETTINGS_PAGE.monitoringSchedule }
          element={
            <PermissionRoute requiredRoles={ [ROLE.priceAnalyticsSpecialist] }>
              <MonitoringSchedulePage />
            </PermissionRoute>
          }
        />

        <Route
          path={ SETTINGS_PAGE.directoryContractors }
          element={
            <PermissionRoute requiredRoles={ [ROLE.priceAnalyticsSpecialist] }>
              <DirectoryContractorsPage />
            </PermissionRoute>
          }
        />

        <Route
          path={ SETTINGS_PAGE.managingSubtypes }
          element={
            <PermissionRoute requiredRoles={ [ROLE.priceAnalyticsSpecialist] }>
              <ManagingSubtypesPage />
            </PermissionRoute>
          }
        />

        <Route
          path={ SETTINGS_PAGE.managingMonitorings }
          element={
            <PermissionRoute requiredRoles={ [ROLE.priceAnalyticsSpecialist] }>
              <ManagingMonitoringsPage />
            </PermissionRoute>
          }
        />

        <Route
          path={ SETTINGS_PAGE.templatesEditor }
          element={
            <PermissionRoute requiredRoles={ [ROLE.priceAnalyticsSpecialist] }>
              <TemplatesEditorPage />
            </PermissionRoute>
          }
        />

        <Route
          path={ SETTINGS_PAGE.checkingPricesCompetitors }
          element={
            <PermissionRoute requiredRoles={ [ROLE.priceAnalyticsSpecialist] }>
              <CheckingPricesCompetitorsPage />
            </PermissionRoute>
          }
        />

        <Route
          path={ SETTINGS_PAGE.classifierControls }
          element={
            <PermissionRoute requiredRoles={ [ROLE.priceAnalyticsSpecialist] }>
              <ClassifierControlsPage />
            </PermissionRoute>
          }
        />
      </Route>
    </Routes>
  )
}

export default App
